import { CloudFunction } from '../../../pure-js/enums/CloudFunction.js'
import { DiscountCode } from '../../../pure-js/types/DiscountCodeTypes.js'
import {
  EarlyBirdBooking,
  EnrollmentRequestResponseV3,
  EnrollmentRequestV3,
  EnrollmentUserGetOrCreateRequest,
  EnrollmentUserGetOrCreateResponse
} from '../../../pure-js/types/GrooverTypes.js'
import {
  CreatePaymentTransactionFromDropinTicketInput,
  CreatePaymentTransactionFromDropinTicketOutput
} from '../../../pure-js/types/PaymentTransactionTypes.js'
import {
  AddSsnForDropinTicketRequest,
  AddSsnForEnrollmentUserRequest,
  AddSsnForPrivateLessonOrderRequest,
  CheckDiscountCodeRequest,
  CloudFunctionErrorResponse,
  CreateDropinTicketAndInitiateSwishPaymentRequest,
  CreateDropinTicketAndInitiateSwishPaymentResponse,
  CreateDropinTicketForPhoneRequest,
  CreateDropinTicketResponse,
  GetDropinTicketActivationStatusRequest,
  GetDropinTicketActivationStatusResponse,
  CreateEarlyBirdBookingRequest,
  CreatePaymentIntentResponse,
  GetActivePeriodsForUserRequest,
  GetActivePeriodsForUserResponse,
  GetUserPreviousDiscountsRequest,
  GetUserPreviousDiscountsResponse,
  GetCreditForUserRequest,
  GetCreditForUserResponse,
  PayForGroupRequest,
  PayForGroupResponse,
  PayForGroupWithUserCreditRequest,
  PayForGroupWithUserCreditResponse,
  PayForGroupWithEpassiRequest,
  PayForGroupWithEpassiResponse,
  PayForGroupWithSwishRequest,
  PayForGroupWithSwishResponse,
  PayForGroupWithVippsRequest,
  PayForGroupWithVippsResponse,
  GetPrivateLessonConfigRequest,
  GetPrivateLessonConfigResponse,
  GetPrivateLessonOrderRequest,
  GetPrivateLessonOrderResponse,
  GetObfuscatedEnrollmentResponse,
  ObfuscatedEnrollmentRequest,
  CreatePrivateLessonOrderRequest,
  CreatePrivateLessonOrderResponse,
  SendPrivateLessonsPaymentSuccessEmailRequest,
  SendPrivateLessonsPaymentSuccessEmailResponse,
  PayWithUserCreditRequest,
  CreateStripePaymentIntentForDropinTicketRequest,
  CreateStripePaymentIntentForDropinTicketResponse,
  CreateStripePaymentIntentForPrivateLessonOrderRequest,
  CheckUnfinishedEnrollmentRequest,
  CheckUnfinishedEnrollmentResponse,
  GetActivityPublicCapacityRequest,
  GetActivityPublicCapacityResponse,
  FreeDropinRewardPreviewRequest,
  FreeDropinRewardPreviewResponse,
  SendSlackNotificationRequest,
  SendSlackNotificationResponse,
  StartPortalViewAsRequest,
  StartPortalViewAsResponse,
  RedeemPortalViewAsRequest,
  RedeemPortalViewAsResponse,
  GetPortalViewAsSessionRequest,
  GetPortalViewAsSessionResponse,
  EndPortalViewAsRequest,
  EndPortalViewAsResponse
} from '../../../pure-js/types/QueryParamTypes.js'
import { CreateStripePaymentIntentWebRequest } from '../../../pure-js/types/StripeTypes.js'
import {
  CancelPaymentInput,
  CreatePaymentForPaymentTransactionInput,
  CreatePaymentForPrivateLessonOrderInput,
  CreatePaymentInput,
  SwishPaymentOKResponse,
  SwishResponseError
} from '../../../pure-js/types/SwishTypes.js'
import {
  CancelVippsPaymentRequest,
  CreateVippsPaymentRequest,
  CreateVippsSessionCheckoutRequest,
  VippsPaymentOKResponse,
  VippsResponseError,
  VippsSessionCheckoutOKResponse
} from '../../../pure-js/types/VippsMobileTypes.js'
import {
  FritidskortetLookupOrganizationRequest,
  FritidskortetLookupOrganizationResponse,
  FritidskortetSubmitPaymentDetailsRequest,
  FritidskortetSubmitPaymentDetailsResponse,
  FritidskortetGetPaymentStatusRequest,
  FritidskortetGetPaymentStatusResponse,
  FritidskortetConfirmPayoutRequest,
  FritidskortetConfirmPayoutResponse
} from '../../../pure-js/types/FritidskortetTypes.js'
import { EpassiInitiatePaymentRequest, EpassiInitiatePaymentResponse } from '../../../pure-js/types/EpassiTypes.js'
import { callFunction } from './CloudFunctionsApiHandlerHelper.js'

export const createStripePaymentIntent = (
  req: CreateStripePaymentIntentWebRequest
): Promise<CloudFunctionErrorResponse | CreatePaymentIntentResponse> =>
  callFunction(CloudFunction.createStripePaymentIntent, req)

export const getObfuscatedEnrollment = (
  req: ObfuscatedEnrollmentRequest
): Promise<GetObfuscatedEnrollmentResponse | CloudFunctionErrorResponse | void> =>
  callFunction(CloudFunction.getObfuscatedEnrollment, req)

export const getFreeDropinRewardPreview = (
  req: FreeDropinRewardPreviewRequest
): Promise<FreeDropinRewardPreviewResponse> =>
  callFunction(CloudFunction.getFreeDropinRewardPreview, req) as Promise<FreeDropinRewardPreviewResponse>

export const saveUserRegistrationV3 = (req: EnrollmentRequestV3): Promise<EnrollmentRequestResponseV3> =>
  callFunction(CloudFunction.saveUserRegistrationV3, req) as Promise<EnrollmentRequestResponseV3>

export const saveUserRegistrationV4 = (req: EnrollmentRequestV3): Promise<EnrollmentRequestResponseV3> =>
  callFunction(CloudFunction.saveUserRegistrationV4, req) as Promise<EnrollmentRequestResponseV3>

export const payForGroup = (req: PayForGroupRequest): Promise<PayForGroupResponse> =>
  callFunction(CloudFunction.payForGroup, req) as Promise<PayForGroupResponse>

export const payForGroupWithUserCredit = (
  req: PayForGroupWithUserCreditRequest
): Promise<PayForGroupWithUserCreditResponse> =>
  callFunction(CloudFunction.payForGroupWithUserCredit, req) as Promise<PayForGroupWithUserCreditResponse>

/**
 * The origin this page is served on, stamped onto every payment-initiating call so the provider
 * returns the payer here rather than to the hardcoded production host. Absent (server render) the
 * server falls back to production, so this degrades a return URL, it never fails a payment.
 */
const getReturnOrigin = (): string | undefined =>
  typeof window !== 'undefined' && window.location?.origin ? window.location.origin : undefined

/** Adds `returnOrigin` without overwriting one a caller set deliberately. */
const withReturnOrigin = <T extends { returnOrigin?: string }>(req: T): T =>
  req.returnOrigin ? req : { ...req, returnOrigin: getReturnOrigin() }

export const payForGroupWithEpassi = (req: PayForGroupWithEpassiRequest): Promise<PayForGroupWithEpassiResponse> =>
  callFunction(CloudFunction.payForGroupWithEpassi, withReturnOrigin(req)) as Promise<PayForGroupWithEpassiResponse>

export const payForGroupWithSwish = (req: PayForGroupWithSwishRequest): Promise<PayForGroupWithSwishResponse> =>
  callFunction(CloudFunction.payForGroupWithSwish, req) as Promise<PayForGroupWithSwishResponse>

export const payForGroupWithVipps = (req: PayForGroupWithVippsRequest): Promise<PayForGroupWithVippsResponse> =>
  callFunction(CloudFunction.payForGroupWithVipps, withReturnOrigin(req)) as Promise<PayForGroupWithVippsResponse>

export const createSwishPaymentIntent = (
  req: CreatePaymentInput
): Promise<SwishPaymentOKResponse | SwishResponseError> => callFunction(CloudFunction.createSwishPaymentIntent, req)

export const cancelSwishPaymentIntent = (
  req: CancelPaymentInput
): Promise<SwishPaymentOKResponse | SwishResponseError> => callFunction(CloudFunction.cancelSwishPaymentIntent, req)

export const createVippsPayment = (
  req: CreateVippsPaymentRequest
): Promise<VippsPaymentOKResponse | VippsResponseError> =>
  callFunction(CloudFunction.createVippsPayment, withReturnOrigin(req))

export const cancelVippsPayment = (
  req: CancelVippsPaymentRequest
): Promise<VippsPaymentOKResponse | VippsResponseError> => callFunction(CloudFunction.cancelVippsPayment, req)

export const getActivePeriodsForUser = (
  req: GetActivePeriodsForUserRequest
): Promise<GetActivePeriodsForUserResponse> => callFunction(CloudFunction.getActivePeriodsForUser, req)

export const getUserPreviousDiscounts = (
  req: GetUserPreviousDiscountsRequest
): Promise<GetUserPreviousDiscountsResponse> => callFunction(CloudFunction.getUserPreviousDiscounts, req)

export const addSsnForEnrollmentUser = (req: AddSsnForEnrollmentUserRequest): Promise<CloudFunctionErrorResponse> =>
  callFunction(CloudFunction.addSsnForEnrollmentUser, req)

export const addSsnForDropinTicket = (req: AddSsnForDropinTicketRequest): Promise<CloudFunctionErrorResponse> =>
  callFunction(CloudFunction.addSsnForDropinTicket, req)

export const addSsnForPrivateLessonOrder = (
  req: AddSsnForPrivateLessonOrderRequest
): Promise<CloudFunctionErrorResponse> => callFunction(CloudFunction.addSsnForPrivateLessonOrder, req)

export const getCreditForUser = (req: GetCreditForUserRequest): Promise<GetCreditForUserResponse> =>
  callFunction(CloudFunction.getCreditForUser, req)

export const payWithUserCredit = (req: PayWithUserCreditRequest): Promise<boolean> =>
  callFunction(CloudFunction.payWithUserCredit, req)

export const createEarlyBirdBooking = (req: CreateEarlyBirdBookingRequest): Promise<EarlyBirdBooking> =>
  callFunction(CloudFunction.createEarlyBirdBooking, req)

export const createVippsSessionCheckout = (
  req: CreateVippsSessionCheckoutRequest
): Promise<VippsSessionCheckoutOKResponse | VippsResponseError> =>
  callFunction(CloudFunction.createVippsSessionCheckout, withReturnOrigin(req))

export const getOrCreateUser = (req: EnrollmentUserGetOrCreateRequest): Promise<EnrollmentUserGetOrCreateResponse> =>
  callFunction(CloudFunction.getOrCreateUser, req)

export const createDropinTicketForPhone = (
  req: CreateDropinTicketForPhoneRequest
): Promise<CreateDropinTicketResponse | CloudFunctionErrorResponse> =>
  callFunction(CloudFunction.createDropinTicketForPhone, req)

export const createDropinTicketAndInitiateSwishPayment = (
  req: CreateDropinTicketAndInitiateSwishPaymentRequest
): Promise<CreateDropinTicketAndInitiateSwishPaymentResponse | CloudFunctionErrorResponse> =>
  callFunction(CloudFunction.createDropinTicketAndInitiateSwishPayment, req)

export const getDropinTicketActivationStatus = (
  req: GetDropinTicketActivationStatusRequest
): Promise<GetDropinTicketActivationStatusResponse | CloudFunctionErrorResponse> =>
  callFunction(CloudFunction.getDropinTicketActivationStatus, req)

export const createPaymentTransactionFromDropinTicket = (req: CreatePaymentTransactionFromDropinTicketInput) =>
  callFunction(
    CloudFunction.createPaymentTransactionFromDropinTicket,
    req
  ) as Promise<CreatePaymentTransactionFromDropinTicketOutput>

export const createSwishPaymentIntentForPaymentTransaction = (req: CreatePaymentForPaymentTransactionInput) =>
  callFunction(CloudFunction.createSwishPaymentIntentForPaymentTransaction, req) as Promise<
    SwishPaymentOKResponse | SwishResponseError
  >

export const createSwishPaymentIntentForPrivateLessonOrder = (req: CreatePaymentForPrivateLessonOrderInput) =>
  callFunction(CloudFunction.createSwishPaymentIntentForPrivateLessonOrder, req) as Promise<
    SwishPaymentOKResponse | SwishResponseError
  >

export const checkDiscountCode = (req: CheckDiscountCodeRequest) =>
  callFunction(CloudFunction.checkDiscountCode, req) as Promise<DiscountCode | null>

export const sendPrivateLessonsPaymentSuccessEmail = (
  req: SendPrivateLessonsPaymentSuccessEmailRequest
): Promise<SendPrivateLessonsPaymentSuccessEmailResponse> =>
  callFunction(CloudFunction.sendPrivateLessonsPaymentSuccessEmail, req)

export const getPrivateLessonConfig = (req: GetPrivateLessonConfigRequest): Promise<GetPrivateLessonConfigResponse> =>
  callFunction(CloudFunction.getPrivateLessonConfig, req)

export const getPrivateLessonOrder = (req: GetPrivateLessonOrderRequest): Promise<GetPrivateLessonOrderResponse> =>
  callFunction(CloudFunction.getPrivateLessonOrder, req)

export const createPrivateLessonOrder = (
  req: CreatePrivateLessonOrderRequest
): Promise<CreatePrivateLessonOrderResponse> => callFunction(CloudFunction.createPrivateLessonOrder, req)

// Portal Authentication
export interface RequestPortalLoginSMSRequest {
  phoneNumber: string
}

export interface RequestPortalLoginSMSResponse {
  success: boolean
  messageId?: string
  sessionInfo?: string
  minutesRemaining?: number
  enrollmentUserId?: string
}

export const requestPortalLoginSMS = (req: RequestPortalLoginSMSRequest): Promise<RequestPortalLoginSMSResponse> =>
  callFunction(CloudFunction.requestPortalLoginSMS, req)

export interface RequestPortalLoginEmailLinkRequest {
  phoneNumber: string
  email: string
}

export interface RequestPortalLoginEmailLinkResponse {
  success: boolean
  messageId?: string
  minutesRemaining?: number
  enrollmentUserId?: string
}

export const requestPortalLoginEmailLink = (
  req: RequestPortalLoginEmailLinkRequest
): Promise<RequestPortalLoginEmailLinkResponse> =>
  callFunction(CloudFunction.requestPortalLoginEmailLink, { ...req, source: 'portal' })

export interface SetPortalUserClaimsRequest {
  uid: string
  enrollmentUserId?: string
  email?: string
}

export interface SetPortalUserClaimsResponse {
  success: boolean
}

export const setPortalUserClaims = (req: SetPortalUserClaimsRequest): Promise<SetPortalUserClaimsResponse> =>
  callFunction(CloudFunction.setPortalUserClaims, req)

// Fritidskortet (EHM)
export const fritidskortetLookupOrganization = (
  req: FritidskortetLookupOrganizationRequest
): Promise<FritidskortetLookupOrganizationResponse | { isError: true; message: string }> =>
  callFunction(CloudFunction.fritidskortetLookupOrganization, req)

export const fritidskortetSubmitPaymentDetails = (
  req: FritidskortetSubmitPaymentDetailsRequest
): Promise<FritidskortetSubmitPaymentDetailsResponse | { isError: true; message: string }> =>
  callFunction(CloudFunction.fritidskortetSubmitPaymentDetails, req)

export const fritidskortetGetPaymentStatus = (
  req: FritidskortetGetPaymentStatusRequest
): Promise<FritidskortetGetPaymentStatusResponse | { isError: true; message: string }> =>
  callFunction(CloudFunction.fritidskortetGetPaymentStatus, req)

export const fritidskortetConfirmPayout = (
  req: FritidskortetConfirmPayoutRequest
): Promise<FritidskortetConfirmPayoutResponse | { isError: true; message: string }> =>
  callFunction(CloudFunction.fritidskortetConfirmPayout, req)

// ePassi
export const epassiInitiatePayment = (
  req: EpassiInitiatePaymentRequest
): Promise<EpassiInitiatePaymentResponse | { isError: true; message: string }> =>
  callFunction(CloudFunction.epassiInitiatePayment, withReturnOrigin(req))

export const createStripePaymentIntentForDropinTicket = (
  req: CreateStripePaymentIntentForDropinTicketRequest
): Promise<CreateStripePaymentIntentForDropinTicketResponse | CloudFunctionErrorResponse> =>
  callFunction(CloudFunction.createStripePaymentIntentForDropinTicket, req)

export const createStripePaymentIntentForPrivateLessonOrder = (
  req: CreateStripePaymentIntentForPrivateLessonOrderRequest
): Promise<CreatePaymentIntentResponse | CloudFunctionErrorResponse> =>
  callFunction(CloudFunction.createStripePaymentIntentForPrivateLessonOrder, req)

export const checkUnfinishedEnrollment = (
  req: CheckUnfinishedEnrollmentRequest
): Promise<CheckUnfinishedEnrollmentResponse> =>
  callFunction(CloudFunction.checkUnfinishedEnrollment, req) as Promise<CheckUnfinishedEnrollmentResponse>

export const getActivityPublicCapacity = (
  req: GetActivityPublicCapacityRequest
): Promise<GetActivityPublicCapacityResponse> =>
  callFunction(CloudFunction.getActivityPublicCapacity, req) as Promise<GetActivityPublicCapacityResponse>

export const sendSlackNotification = (req: SendSlackNotificationRequest): Promise<SendSlackNotificationResponse> =>
  callFunction(CloudFunction.sendSlackNotification, req)

export const startPortalViewAs = (req: StartPortalViewAsRequest): Promise<StartPortalViewAsResponse> =>
  callFunction(CloudFunction.startPortalViewAs, req)

export const redeemPortalViewAs = (req: RedeemPortalViewAsRequest): Promise<RedeemPortalViewAsResponse> =>
  callFunction(CloudFunction.redeemPortalViewAs, req)

export const getPortalViewAsSession = (req: GetPortalViewAsSessionRequest): Promise<GetPortalViewAsSessionResponse> =>
  callFunction(CloudFunction.getPortalViewAsSession, req)

export const endPortalViewAs = (req: EndPortalViewAsRequest): Promise<EndPortalViewAsResponse> =>
  callFunction(CloudFunction.endPortalViewAs, req)
